import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import TabelaLoading from '../../../../components/TabelaLoading';
import TabelaVazia from '../../../../components/TabelaVazia';
import api from '../../../../services/api';
import { moeda, data } from '../../../../util/util';

const PainelParcelaPeriodica = () => {
    const [parcelasPeriodicas, setParcelasPeriodicas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filtros, setFiltros] = useState({
        empresa: '',
        situacao: '',
        periodicidade: ''
    });
    const history = useHistory();

    const carregarParcelasPeriodicas = useCallback(async () => {
        try {
            setLoading(true);
            const response = await api({
                method: 'get',
                action: '/financeiroParcelaPeriodica',
                token: localStorage.getItem('token'),
                params: filtros
            });

            if (response.success) {
                setParcelasPeriodicas(response.data);
            } else {
                toast.error('Erro ao carregar parcelas periódicas');
            }
        } catch (error) {
            console.error('Erro:', error);
            toast.error('Erro ao carregar parcelas periódicas');
        } finally {
            setLoading(false);
        }
    }, [filtros]);

    useEffect(() => {
        carregarParcelasPeriodicas();
    }, [carregarParcelasPeriodicas]);

    const handlePausar = async (id) => {
        try {
            const response = await api({
                method: 'put',
                action: `/financeiroParcelaPeriodica/pausar/${id}`,
                token: localStorage.getItem('token')
            });

            if (response.success) {
                toast.success('Parcela periódica pausada com sucesso');
                carregarParcelasPeriodicas();
            } else {
                toast.error('Erro ao pausar parcela periódica');
            }
        } catch (error) {
            console.error('Erro:', error);
            toast.error('Erro ao pausar parcela periódica');
        }
    };

    const handleAtivar = async (id) => {
        try {
            const response = await api({
                method: 'put',
                action: `/financeiroParcelaPeriodica/ativar/${id}`,
                token: localStorage.getItem('token')
            });

            if (response.success) {
                toast.success('Parcela periódica ativada com sucesso');
                carregarParcelasPeriodicas();
            } else {
                toast.error('Erro ao ativar parcela periódica');
            }
        } catch (error) {
            console.error('Erro:', error);
            toast.error('Erro ao ativar parcela periódica');
        }
    };

    const handleExcluir = async (id) => {
        if (window.confirm('Tem certeza que deseja excluir esta parcela periódica?')) {
            try {
                const response = await api({
                    method: 'delete',
                    action: `/financeiroParcelaPeriodica/${id}`,
                    token: localStorage.getItem('token')
                });

                if (response.success) {
                    toast.success('Parcela periódica excluída com sucesso');
                    carregarParcelasPeriodicas();
                } else {
                    toast.error('Erro ao excluir parcela periódica');
                }
            } catch (error) {
                console.error('Erro:', error);
                toast.error('Erro ao excluir parcela periódica');
            }
        }
    };

    const handleEditar = (id) => {
        history.push(`/financeiro/parcela-periodica/editar/${id}`);
    };

    const handleNova = () => {
        history.push('/financeiro/parcela-periodica/nova');
    };

    const getSituacaoBadge = (situacao) => {
        const badges = {
            'ativa': <span className="badge badge-success">Ativa</span>,
            'pausada': <span className="badge badge-warning">Pausada</span>,
            'cancelada': <span className="badge badge-danger">Cancelada</span>
        };
        return badges[situacao] || <span className="badge badge-secondary">{situacao}</span>;
    };

    const getPeriodicidadeLabel = (periodicidade) => {
        const labels = {
            'mensal': 'Mensal',
            'anual': 'Anual'
        };
        return labels[periodicidade] || periodicidade;
    };

    const columns = [
        {
            name: 'Descrição',
            selector: row => row.descricao,
            sortable: true,
            width: '250px',
            cell: (row) => (
                <div>
                    <strong>{row.descricao}</strong>
                    {row.observacao && <p className="text-muted small mb-0">{row.observacao}</p>}
                </div>
            )
        },
        {
            name: 'Favorecido',
            selector: row => row.participante?.nome,
            sortable: true,
            width: '200px',
            cell: (row) => (
                <div>
                    <strong>{row.participante?.nome}</strong>
                    {row.participante?.cpf && <p className="text-muted small mb-0">{row.participante.cpf}</p>}
                </div>
            )
        },
        {
            name: 'Valor',
            selector: row => row.valor,
            sortable: true,
            width: '120px',
            cell: (row) => (
                <div className="text-right">
                    <strong>{moeda(row.valor)}</strong>
                </div>
            )
        },
        {
            name: 'Periodicidade',
            selector: row => row.periodicidade,
            sortable: true,
            width: '120px',
            cell: (row) => (
                <div>
                    <strong>{getPeriodicidadeLabel(row.periodicidade)}</strong>
                    <p className="text-muted small mb-0">
                        {row.periodicidade === 'mensal'
                            ? `Dia ${row.dia_vencimento}`
                            : `${row.dia_vencimento}/${row.mes_vencimento}`
                        }
                    </p>
                </div>
            )
        },
        {
            name: 'Período',
            selector: row => row.data_inicio,
            sortable: true,
            width: '150px',
            cell: (row) => (
                <div>
                    <div><strong>Início:</strong> {data(row.data_inicio)}</div>
                    <div><strong>Fim:</strong> {data(row.data_fim)}</div>
                </div>
            )
        },
        {
            name: 'Situação',
            selector: row => row.situacao,
            sortable: true,
            width: '100px',
            cell: (row) => getSituacaoBadge(row.situacao)
        },
        {
            name: 'Ações',
            width: '200px',
            cell: (row) => (
                <div className="btn-group" role="group">
                    <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => handleEditar(row.id)}
                        title="Editar"
                    >
                        <i className="fas fa-edit"></i>
                    </button>

                    {row.situacao === 'ativa' && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-warning"
                            onClick={() => handlePausar(row.id)}
                            title="Pausar"
                        >
                            <i className="fas fa-pause"></i>
                        </button>
                    )}

                    {row.situacao === 'pausada' && (
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-success"
                            onClick={() => handleAtivar(row.id)}
                            title="Ativar"
                        >
                            <i className="fas fa-play"></i>
                        </button>
                    )}

                    <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleExcluir(row.id)}
                        title="Excluir"
                    >
                        <i className="fas fa-trash"></i>
                    </button>
                </div>
            )
        }
    ];

    return (
        <div className="container-fluid">
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header">
                            <div className="row align-items-center">
                                <div className="col">
                                    <h4 className="card-title mb-0">
                                        <i className="fas fa-calendar-alt mr-2"></i>
                                        Parcelas Periódicas
                                    </h4>
                                </div>
                                <div className="col-auto">
                                    <button
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={handleNova}
                                    >
                                        <i className="fas fa-plus mr-1"></i>
                                        Nova Parcela Periódica
                                    </button>
                                </div>
                            </div>
                        </div>

                        <div className="card-body">
                            {/* Filtros */}
                            <div className="row mb-3">
                                <div className="col-md-3">
                                    <label className="form-label">Situação</label>
                                    <select
                                        className="form-control"
                                        value={filtros.situacao}
                                        onChange={(e) => setFiltros({ ...filtros, situacao: e.target.value })}
                                    >
                                        <option value="">Todas</option>
                                        <option value="ativa">Ativa</option>
                                        <option value="pausada">Pausada</option>
                                        <option value="cancelada">Cancelada</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">Periodicidade</label>
                                    <select
                                        className="form-control"
                                        value={filtros.periodicidade}
                                        onChange={(e) => setFiltros({ ...filtros, periodicidade: e.target.value })}
                                    >
                                        <option value="">Todas</option>
                                        <option value="mensal">Mensal</option>
                                        <option value="anual">Anual</option>
                                    </select>
                                </div>
                            </div>

                            {/* Tabela */}
                            <div className="table-responsive">
                            <DataTable
                                title=""
                                columns={columns}
                                data={parcelasPeriodicas}
                                progressPending={loading}
                                persistTableHead
                                progressComponent={<TabelaLoading />}
                                pagination
                                paginationPerPage={20}
                                paginationRowsPerPageOptions={[10, 20, 50, 100]}
                                highlightOnHover
                                pointerOnHover
                                responsive
                                noDataComponent={<TabelaVazia texto="Nenhuma parcela periódica encontrada" />}
                            />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PainelParcelaPeriodica;
